import { useCallback, useRef, useState } from 'react'
import type { DocumentNode } from 'graphql'

import type { AnyGraphqlDataCollectionSource } from 'modules/integrations/hooks/SyncContext'

export interface DataCollectionSourceState {
  document: DocumentNode | null
  mappedVariablesJson: string | null
  mappedBaseVariablesJson: string | null
}

const EMPTY_DATA_COLLECTION_SOURCE_STATE: DataCollectionSourceState = {
  document: null,
  mappedVariablesJson: null,
  mappedBaseVariablesJson: null,
}

const toState = (source: AnyGraphqlDataCollectionSource): DataCollectionSourceState => ({
  document: source.document,
  mappedVariablesJson: source.mappedVariables ? JSON.stringify(source.mappedVariables) : null,
  mappedBaseVariablesJson: source.mappedBaseVariables
    ? JSON.stringify(source.mappedBaseVariables)
    : null,
})

const isSameState = (a: DataCollectionSourceState, b: DataCollectionSourceState) =>
  a.document === b.document &&
  a.mappedVariablesJson === b.mappedVariablesJson &&
  a.mappedBaseVariablesJson === b.mappedBaseVariablesJson

export const useDataCollectionSourceState = () => {
  const [state, setState] = useState<DataCollectionSourceState>(EMPTY_DATA_COLLECTION_SOURCE_STATE)
  const ref = useRef<AnyGraphqlDataCollectionSource | null>(null)

  const setSource = useCallback((source: AnyGraphqlDataCollectionSource) => {
    ref.current = source
    const next = toState(source)
    setState((prev) => (isSameState(prev, next) ? prev : next))
  }, [])

  return { state, ref, setSource }
}
